import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { listOperatorRestaurants, type OperatorRestaurantsPageDto } from '../api/operator'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { operatorRestaurantPath, operatorRestaurantRfqsPath } from '../operator/paths'
import { formatCityLabel } from '../utils/cityLabel'

const restaurantLabel = (restaurant: { trade_name: string | null; legal_name: string }) =>
  restaurant.trade_name?.trim() || restaurant.legal_name

export const OperatorRestaurantsScreen = () => {
  const [pageIndex, setPageIndex] = useState(0)
  const [page, setPage] = useState<OperatorRestaurantsPageDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')

  const loadPage = useCallback(async (index: number, query?: string) => {
    setLoading(true)
    setError(null)
    try {
      const result = await listOperatorRestaurants(index, 50, query)
      setPage(result)
      setPageIndex(index)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить рестораны')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadPage(0, search)
  }, [loadPage, search])

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault()
    setSearch(searchInput.trim())
  }

  return (
    <section className="market-page">
      <MarketPageHeader title="Рестораны" description="Организации-рестораны на платформе" />

      <form className="market-toolbar market-toolbar--controls" onSubmit={handleSearchSubmit}>
        <label className="echat-field market-toolbar__field">
          <span className="visually-hidden">Поиск</span>
          <input
            className="echat-field__input"
            type="search"
            placeholder="Название, ИНН или город"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>
        <button type="submit" className="echat-btn echat-btn--ghost">
          Найти
        </button>
      </form>

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && page?.content.length === 0 ? (
        <MarketEmptyState title="Рестораны не найдены" description="Попробуйте изменить поисковый запрос." />
      ) : null}

      {page && page.content.length > 0 ? (
        <div className="market-table-wrap">
          <table className="market-table">
            <thead>
              <tr>
                <th>Ресторан</th>
                <th>Юр. название</th>
                <th>ИНН</th>
                <th>Город</th>
                <th>Заявок</th>
                <th>Статус</th>
                <th aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {page.content.map((restaurant) => (
                <tr key={restaurant.id}>
                  <td className="market-table__cell--truncate">{restaurantLabel(restaurant)}</td>
                  <td className="market-table__cell--truncate">{restaurant.legal_name}</td>
                  <td>{restaurant.inn ?? '—'}</td>
                  <td>{formatCityLabel(restaurant.city)}</td>
                  <td>{restaurant.rfq_count}</td>
                  <td>
                    {restaurant.active ? (
                      <span className="echat-badge echat-badge--success">Активен</span>
                    ) : (
                      <span className="echat-badge echat-badge--muted">Неактивен</span>
                    )}
                  </td>
                  <td>
                    <div className="market-inline-actions">
                      <Link
                        to={operatorRestaurantPath(restaurant.id)}
                        className="echat-btn echat-btn--ghost echat-btn--sm"
                      >
                        Карточка
                      </Link>
                      <Link
                        to={operatorRestaurantRfqsPath(restaurant.id)}
                        className="echat-btn echat-btn--ghost echat-btn--sm"
                      >
                        Заявки
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {page && page.totalPages > 1 ? (
        <div className="market-pagination">
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={pageIndex <= 0 || loading}
            onClick={() => void loadPage(pageIndex - 1, search)}
          >
            ←
          </button>
          <span>
            Страница {pageIndex + 1} из {page.totalPages}
          </span>
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            disabled={pageIndex >= page.totalPages - 1 || loading}
            onClick={() => void loadPage(pageIndex + 1, search)}
          >
            →
          </button>
        </div>
      ) : null}
    </section>
  )
}
